import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getShipment, updateShipment, getContainers, createContainer, updateContainer,
  deleteContainer, createContainersBulk, getEvents, createEvent, deleteEvent,
  getNotes, createNote, deleteNote, getCatalog, getClients, getImporters,
  getSuppliers, uploadCostBreakdown, createCatalogItem, syncShipmentNow,
  deleteShipment, revalidateShipment, changeShipmentMbl, consultSoiaForShipment,
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { StatusBadge, TrafficLight, CostBadge } from '../components/ui/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { formatDate, formatDateTime, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, Calendar, FileText, Clock,
  Upload, X, CheckCircle, AlertTriangle, Eye, DollarSign, History, Anchor, RefreshCw, Loader2, ShieldCheck, Pencil,
} from 'lucide-react';
import CostBreakdownPanel from '../components/CostBreakdownPanel';
import AuditHistoryPanel from '../components/AuditHistoryPanel';
import { PedimentoPanel } from '../components/PedimentoPanel';
import { DocumentsPanel } from '../components/DocumentsPanel';
import { ContractPanel } from '../components/ContractPanel';
import { InvoicePLGenerator } from '../components/InvoicePLGenerator';
import { CartaExplicacionGenerator } from '../components/CartaExplicacionGenerator';
import { ContratoGenerator } from '../components/ContratoGenerator';
import { ContainerTable } from '../components/ContainerTable';
import { ShipmentJourney } from '../components/ShipmentJourney';
import { EventsPanel } from '../components/EventsPanel';
import { NotesPanel } from '../components/NotesPanel';
import { PublicLinkButton } from '../components/PublicLinkButton';
import { DiagnoseButton } from '../components/DiagnoseButton';

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    // Check on mount
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
}

export default function ShipmentDetailPage() {
  const { mbl: rawMbl } = useParams();
  const navigate = useNavigate();
  const { effectiveRole, canManage, clientViewMode, hasField, user, hasFeature } = useAuth();
  const { t } = useI18n();
  const isMobile = useIsMobile();
  
  // Clean MBL - remove special characters and spaces
  const mbl = rawMbl ? rawMbl.trim().replace(/\s+/g, '').replace(/\u00a0/g, '') : '';
  
  const [shipment, setShipment] = useState(null);
  const [containers, setContainers] = useState([]);
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientConsultingSoia, setClientConsultingSoia] = useState(false);

  const handleClientConsultSoia = async () => {
    setClientConsultingSoia(true);
    const tid = toast.loading('Consultando SOIA (SAT)... esto puede tardar hasta 1 min');
    try {
      const res = await consultSoiaForShipment(mbl);
      toast.success(res.data?.message || 'Consulta SOIA completada', { id: tid, duration: 8000 });
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al consultar SOIA', { id: tid });
    } finally {
      setClientConsultingSoia(false);
    }
  };
  const [syncing, setSyncing] = useState(false);
  
  // Catalogs & lookups
  const [shipmentStatuses, setShipmentStatuses] = useState([]);
  const [containerStatuses, setContainerStatuses] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [clients, setClients] = useState([]);
  const [importers, setImporters] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [puertos, setPuertos] = useState([]);
  
  // Form states
  const [editForm, setEditForm] = useState({});
  const [showAddPuerto, setShowAddPuerto] = useState(false);
  const [newPuertoName, setNewPuertoName] = useState('');
  const [newEvent, setNewEvent] = useState({ event_type_code: '', event_date: '', note: '', visible_to_client: true, container_number: 'bl' });
  const [newNote, setNewNote] = useState({ note: '', container_number: 'bl' });
  const [costImage, setCostImage] = useState(null);

  // Lazy-mount tracker: panels for costs/pedimentos/documents/history only
  // mount once the user opens their tab. info/containers/events/notes are
  // pre-loaded by the page-level loadAll() and use already-fetched state.
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(['info']));

  // Edit MBL (rename BL number) - admin only
  const [editMblOpen, setEditMblOpen] = useState(false);
  const [newMbl, setNewMbl] = useState('');
  const [renamingMbl, setRenamingMbl] = useState(false);

  const isReadOnly = effectiveRole === 'client';
  const canEditMbl = effectiveRole === 'admin';

  const handleOpenEditMbl = () => {
    setNewMbl(shipment?.mbl || '');
    setEditMblOpen(true);
  };

  const handleConfirmEditMbl = async () => {
    const trimmed = (newMbl || '').trim().toUpperCase();
    if (!trimmed || trimmed === shipment?.mbl) {
      setEditMblOpen(false);
      return;
    }
    setRenamingMbl(true);
    try {
      const res = await changeShipmentMbl(shipment.mbl, trimmed);
      toast.success(res.data?.message || 'MBL actualizado');
      setEditMblOpen(false);
      // Navigate to the new MBL URL since the route param changed
      navigate(`/shipments/${encodeURIComponent(trimmed)}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cambiar el MBL');
    } finally {
      setRenamingMbl(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [mbl]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [
        shipmentRes, containersRes, eventsRes,
        shipmentStatusRes, containerStatusRes, eventTypesRes, incotermsRes,
        clientsRes, importersRes, suppliersRes, puertosRes,
      ] = await Promise.all([
        getShipment(mbl),
        getContainers(mbl),
        getEvents(mbl),
        getCatalog('shipment_status'),
        getCatalog('container_status'),
        getCatalog('event_types'),
        getCatalog('incoterms'),
        canManage ? getClients() : Promise.resolve({ data: [] }),
        canManage ? getImporters() : Promise.resolve({ data: [] }),
        canManage ? getSuppliers() : Promise.resolve({ data: [] }),
        getCatalog('puertos'),
      ]);
      
      setShipment(shipmentRes.data);
      setEditForm(shipmentRes.data);
      setContainers(containersRes.data);
      setEvents(eventsRes.data);
      
      setShipmentStatuses(shipmentStatusRes.data);
      setContainerStatuses(containerStatusRes.data);
      setEventTypes(eventTypesRes.data);
      setIncoterms(incotermsRes.data);
      setClients(clientsRes.data);
      setImporters(importersRes.data);
      setSuppliers(suppliersRes.data);
      setPuertos(puertosRes.data);
      
      // Load notes for all users (backend filters by visibility for clients)
      try {
        const notesRes = await getNotes(mbl);
        setNotes(notesRes.data);
      } catch (err) {
        // Client may get empty if no visible notes yet
        setNotes([]);
      }
    } catch (err) {
      console.error('Error loading shipment:', err);
      toast.error(t('shipments.errorLoadingShipment'));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAddPuerto = async () => {
    if (!newPuertoName.trim()) return;
    try {
      const code = newPuertoName.trim().toUpperCase().replace(/\s+/g, '_').normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const res = await createCatalogItem('puertos', { 
        code, 
        label: newPuertoName.trim(), 
        sort_order: puertos.length + 1, 
        is_active: true 
      });
      setPuertos([...puertos, res.data]);
      setEditForm({ ...editForm, puerto: res.data.label });
      setNewPuertoName('');
      setShowAddPuerto(false);
      toast.success(t('shipments.portCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorCreatingPort'));
    }
  };

  const reloadWithoutEditForm = async () => {
    // Reload shipment/containers/events WITHOUT overwriting editForm.
    // Used by polling during sync to avoid clobbering user's unsaved edits.
    try {
      const [shipmentRes, containersRes, eventsRes] = await Promise.all([
        getShipment(mbl),
        getContainers(mbl),
        getEvents(mbl),
      ]);
      setShipment(shipmentRes.data);
      setContainers(containersRes.data);
      setEvents(eventsRes.data);
    } catch { /* ignore */ }
  };

  const handleSyncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    const toastId = toast.loading('Sincronización iniciada en segundo plano...');
    try {
      await syncShipmentNow(mbl);
      toast.success('Sync iniciado. Recarga esta pantalla en 30-60s para ver cambios.', { id: toastId, duration: 6000 });
      // Poll every 15s — but DO NOT overwrite editForm so unsaved edits (ETA Real) are preserved
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts += 1;
        await reloadWithoutEditForm();
        if (attempts > 8) {
          clearInterval(interval);
          setSyncing(false);
        }
      }, 15000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al sincronizar', { id: toastId });
      setSyncing(false);
    }
  };

  const handleSaveShipment = async () => {
    setSaving(true);
    try {
      const updateData = {
        client_id: editForm.client_id || null,
        origin: editForm.origin || null,
        destination: editForm.destination || null,
        puerto: editForm.puerto || null,
        incoterm_code: editForm.incoterm_code || null,
        vessel_name: editForm.vessel_name || null,
        cargo_type: editForm.cargo_type || null,
        etd_final_destination: editForm.etd_final_destination || null,
        eta_naviera: editForm.eta_naviera || null,
        eta_real: editForm.eta_real || null,
        eta_port: editForm.eta_port || null,
        eta_final: editForm.eta_final || null,
        delivered_at: editForm.delivered_at || null,
        docs_sent_at: editForm.docs_sent_at || null,
        status_general_code: editForm.status_general_code,
        importer_id: editForm.importer_id === 'none' ? null : (editForm.importer_id || null),
        supplier_id: editForm.supplier_id === 'none' ? null : (editForm.supplier_id || null),
        final_quote_done: editForm.final_quote_done || false,
        arnulfo_applies: editForm.arnulfo_applies || false,
        arnulfo_status: editForm.arnulfo_status || null,
        is_active: editForm.is_active !== false,
        total_cost: editForm.total_cost ? parseFloat(editForm.total_cost) : null,
        cost_calculated: editForm.cost_calculated || false,
      };
      
      // Filter out null values to avoid sending them if not changed
      const cleanUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, v]) => v !== undefined)
      );
      
      const res = await updateShipment(mbl, cleanUpdateData);
      setShipment(res.data);
      // Update editForm with new data to stay in sync
      setEditForm(prev => ({...prev, ...res.data}));
      toast.success(t('shipments.savedChanges'));
    } catch (err) {
      console.error('Error saving shipment:', err);
      const errorMsg = err.response?.data?.detail || err.message || t('shipments.errorSaving');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleAddContainersBulk = async (containerNumbers) => {
    const containersToAdd = containerNumbers.map(num => ({
      container_number: num,
      status_container_code: 'NO_SALIDO_CHINA',
      release_traffic_light: 'NA',
    }));
    
    try {
      await createContainersBulk(mbl, containersToAdd);
      toast.success(t('shipments.containersAdded'));
      const res = await getContainers(mbl);
      setContainers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorAddingContainers'));
    }
  };

  const handleUpdateContainer = async (containerId, data, silent = false) => {
    try {
      await updateContainer(mbl, containerId, data);
      if (!silent) {
        const res = await getContainers(mbl);
        setContainers(res.data);
        const shipRes = await getShipment(mbl);
        setShipment(shipRes.data);
        setEditForm(prev => ({...prev, ...shipRes.data}));
        toast.success(t('common.updatedOk'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorUpdatingContainer'));
    }
  };

  // Wrapper that reloads after bulk operations
  const handleUpdateContainerWithReload = async (containerId, data, isBulk = false) => {
    if (isBulk) {
      await handleUpdateContainer(containerId, data, true);
    } else {
      await handleUpdateContainer(containerId, data, false);
    }
  };

  // Reload containers after bulk edit completes (called by ContainerTable)
  const reloadContainersAndShipment = async () => {
    const res = await getContainers(mbl);
    setContainers(res.data);
    const shipRes = await getShipment(mbl);
    setShipment(shipRes.data);
    setEditForm(prev => ({...prev, ...shipRes.data}));
  };

  const handleDeleteContainer = async (containerId) => {
    if (!window.confirm('¿Eliminar este contenedor?')) return;
    try {
      await deleteContainer(mbl, containerId);
      setContainers(containers.filter(c => c.id !== containerId));
      toast.success(t('shipments.containerDeleted'));
    } catch (err) {
      toast.error(t('shipments.errorDeletingContainer'));
    }
  };

  const handleDeleteShipment = async () => {
    const confirmed = window.confirm(`¿Eliminar permanentemente el embarque ${mbl}?\n\nSe borrarán también sus contenedores, eventos, notas y costos. Esta acción no se puede deshacer.`);
    if (!confirmed) return;
    const tid = toast.loading('Eliminando embarque...');
    try {
      await deleteShipment(mbl);
      toast.success(`Embarque ${mbl} eliminado`, { id: tid });
      navigate('/shipments');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al eliminar embarque', { id: tid });
    }
  };

  const handleRevalidateFromDetail = async () => {
    if (!window.confirm(`¿Revalidar todos los contenedores de ${mbl} contra la naviera? Se eliminarán datos falsos si el carrier indica que aún no ha arribado.`)) return;
    const tid = toast.loading('Revalidando...');
    try {
      const res = await revalidateShipment(mbl);
      const d = res?.data || {};
      toast.success(
        `${d.revalidated_ok ?? 0}/${d.total_containers ?? 0} OK · Puerto=${d.deleted_port_records ?? 0} · Ferro=${d.deleted_ferrovalle_records ?? 0}`,
        { id: tid, duration: 7000 }
      );
      await reloadContainersAndShipment();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al revalidar', { id: tid });
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.event_type_code || !newEvent.event_date) {
      toast.error(t('common.required'));
      return;
    }
    try {
      const eventData = {
        ...newEvent,
        container_number: newEvent.container_number === 'bl' ? null : newEvent.container_number,
      };
      await createEvent(mbl, eventData);
      const res = await getEvents(mbl);
      setEvents(res.data);
      setNewEvent({ event_type_code: '', event_date: '', note: '', visible_to_client: true, container_number: 'bl' });
      toast.success(t('shipments.eventCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorCreatingEvent'));
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await deleteEvent(mbl, eventId);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success(t('shipments.eventDeleted'));
    } catch (err) {
      toast.error(t('shipments.errorDeletingContainer'));
    }
  };

  const handleAddNote = async () => {
    if (!newNote.note.trim()) {
      toast.error(t('common.required'));
      return;
    }
    try {
      const noteData = {
        ...newNote,
        container_number: newNote.container_number === 'bl' ? null : newNote.container_number,
      };
      await createNote(mbl, noteData);
      const res = await getNotes(mbl);
      setNotes(res.data);
      setNewNote({ note: '', container_number: 'bl' });
      toast.success(t('shipments.noteCreated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.errorCreatingNote'));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(mbl, noteId);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success(t('shipments.noteDeleted'));
    } catch (err) {
      toast.error(t('shipments.errorDeletingContainer'));
    }
  };

  const handleUploadCostImage = async () => {
    if (!costImage) return;
    try {
      await uploadCostBreakdown(mbl, costImage);
      toast.success(t('shipments.imageUploaded'));
      setCostImage(null);
      loadAll();
    } catch (err) {
      toast.error(t('shipments.errorUploadingImage'));
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">Cargando embarque...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!shipment) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Embarque no encontrado</p>
          <Link to="/shipments">
            <Button variant="outline">Volver a embarques</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="shipment-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-4">
            <Link to="/shipments">
              <Button variant="ghost" size="icon" data-testid="back-btn">
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-2xl sm:text-3xl font-mono break-all">{shipment.mbl}</h1>
                {canEditMbl && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleOpenEditMbl}
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-primary"
                    title="Editar número de MBL"
                    data-testid="edit-mbl-header-btn"
                  >
                    <Pencil size={16} />
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge code={shipment.status_general_code} label={shipment.status_label} />
                {!isReadOnly && shipment.client_name && (
                  <span className="text-sm text-muted-foreground">• {shipment.client_name}</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 pl-12 sm:pl-0">
            <InvoicePLGenerator mbl={mbl} shipment={shipment} containers={containers} />
            <CartaExplicacionGenerator mbl={mbl} shipment={shipment} containers={containers} />
            <ContratoGenerator mbl={mbl} shipment={shipment} containers={containers} />
            <PublicLinkButton mbl={mbl} isReadOnly={isReadOnly} />
            <DiagnoseButton mbl={mbl} isSuperAdmin={user?.is_super_admin || user?.is_master_admin} />
            {!isReadOnly && (
              <Button 
                variant="outline" 
                onClick={handleSyncNow} 
                disabled={syncing}
                data-testid="sync-now-btn"
                title={t('shipments.syncNowTitle')}
              >
                {syncing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
                {syncing ? t('shipments.syncingNow') : t('shipments.syncNow')}
              </Button>
            )}
            {!isReadOnly && (
              <Button onClick={handleSaveShipment} disabled={saving} data-testid="save-shipment-btn">
                <Save size={16} className="mr-2" />
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            )}
          </div>
        </div>

        {/* Mobile-only floating Save bar — keeps the primary action within reach
            while the user scrolls the BL form on a phone. Hidden on md+ screens
            where the header Save button is already visible. */}
        {!isReadOnly && (
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur border-t border-border p-3 flex items-center gap-2 pb-[env(safe-area-inset-bottom)]">
            <Button
              onClick={handleSaveShipment}
              disabled={saving}
              className="flex-1 h-12 text-base"
              data-testid="save-shipment-btn-mobile"
            >
              <Save size={18} className="mr-2" />
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        )}

        {/* Edit MBL Dialog (admin-only) — accessible from any device including mobile */}
        <Dialog open={editMblOpen} onOpenChange={(open) => !renamingMbl && setEditMblOpen(open)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar número de MBL</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">MBL actual</Label>
                <p className="font-mono text-sm break-all">{shipment.mbl}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-mbl-input">Nuevo MBL *</Label>
                <Input
                  id="new-mbl-input"
                  value={newMbl}
                  onChange={(e) => setNewMbl(e.target.value.toUpperCase())}
                  placeholder="MEDUY1234567"
                  className="font-mono uppercase"
                  autoFocus
                  data-testid="new-mbl-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !renamingMbl) handleConfirmEditMbl();
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Se actualizarán contenedores, eventos, notas y costos asociados.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEditMblOpen(false)}
                  disabled={renamingMbl}
                  data-testid="cancel-edit-mbl-btn"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmEditMbl}
                  disabled={renamingMbl || !newMbl?.trim() || newMbl.trim().toUpperCase() === shipment.mbl}
                  data-testid="confirm-edit-mbl-btn"
                >
                  {renamingMbl ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                  {renamingMbl ? 'Guardando…' : 'Guardar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Client View Banner */}
        {clientViewMode && (
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-sm p-3 flex items-center gap-2">
            <Eye size={16} className="text-amber-400" />
            <span className="text-sm text-amber-200">{t('shipments.viewingAsClient')}</span>
          </div>
        )}

        {/* Visual Journey Timeline */}
        <ShipmentJourney
          statusCode={shipment.status_general_code}
          eta={shipment.eta_real || shipment.eta_inicial}
          arrivedAt={shipment.arrived_at}
          deliveredAt={shipment.delivered_at}
          metodoTransporte={containers.find(c => c.metodo_transporte_terrestre)?.metodo_transporte_terrestre}
        />

        {/* Tabs (lazy-mount: heavy panels — costs, pedimentos, documents, history —
            only mount once the user actually clicks their tab. Saves 4-6 fetches on
            initial load when the user just wants to see the BL info). */}
        <Tabs
          defaultValue="info"
          className="space-y-4"
          onValueChange={(value) => setVisitedTabs((prev) => prev.has(value) ? prev : new Set([...prev, value]))}
        >
          {/* Horizontally scrollable on mobile (8+ tabs don't fit). On desktop wraps normally. */}
          <div className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto scrollbar-hide">
            <TabsList className="bg-card border border-border w-max md:w-auto">
              <TabsTrigger value="info" data-testid="tab-info">{t('shipments.information')}</TabsTrigger>
              <TabsTrigger value="containers" data-testid="tab-containers">
                {t('shipments.containers')} ({containers.length})
              </TabsTrigger>
              <TabsTrigger value="events" data-testid="tab-events">
                {t('shipments.timeline')} ({events.length})
              </TabsTrigger>
              {!isReadOnly && hasField('view_costs') && (
                <TabsTrigger value="costs" data-testid="tab-costs">
                  <DollarSign size={14} className="mr-1" />
                  {t('shipments.costs')}
                </TabsTrigger>
              )}
              <TabsTrigger value="notes" data-testid="tab-notes">
                {isReadOnly ? t('shipments.customsSoia') : t('shipments.internalNotes')} ({notes.length})
              </TabsTrigger>
              {!isReadOnly && (
                <TabsTrigger value="pedimentos" data-testid="tab-pedimentos">
                  <FileText size={14} className="mr-1" />
                  {t('shipments.pedimentos')}
                </TabsTrigger>
              )}
              {!isReadOnly && (
                <TabsTrigger value="documents" data-testid="tab-documents">
                  <FileText size={14} className="mr-1" />
                  {t('shipments.documents')}
                </TabsTrigger>
              )}
              {!isReadOnly && (
                <TabsTrigger value="history" data-testid="tab-history">
                  <History size={14} className="mr-1" />
                  {t('shipments.history')}
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* Info Tab */}
          <TabsContent value="info" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* General Info */}
              <div className="bg-card border border-border rounded-sm p-4 space-y-4">
                <h3 className="font-heading text-lg">{t('shipments.generalData')}</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('shipments.originLabel')}</Label>
                    <Input
                      value={editForm.origin || ''}
                      onChange={(e) => setEditForm({...editForm, origin: e.target.value})}
                      disabled={isReadOnly}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t('shipments.destinationFinalLabel')}</Label>
                    <Input
                      value={editForm.destination || ''}
                      onChange={(e) => setEditForm({...editForm, destination: e.target.value})}
                      disabled={isReadOnly}
                      placeholder={t('shipments.destinationPh')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t('shipments.arrivalPort')}</Label>
                    <div className="flex gap-2 mt-1">
                      <Select 
                        value={editForm.puerto || ''} 
                        onValueChange={(v) => setEditForm({...editForm, puerto: v})}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder={t('shipments.selectPort')} />
                        </SelectTrigger>
                        <SelectContent>
                          {puertos.map(p => (
                            <SelectItem key={p.code} value={p.label}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!isReadOnly && (
                        <Dialog open={showAddPuerto} onOpenChange={setShowAddPuerto}>
                          <DialogTrigger asChild>
                            <Button type="button" variant="outline" size="icon" title={t('shipments.addPortTitle')}>
                              <Plus size={16} />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <Anchor size={20} />
                                {t('shipments.addPortBtn')}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-4">
                              <div>
                                <Label>{t('shipments.portName')}</Label>
                                <Input
                                  value={newPuertoName}
                                  onChange={(e) => setNewPuertoName(e.target.value)}
                                  placeholder={t('shipments.portNamePh')}
                                  className="mt-1"
                                  onKeyDown={(e) => e.key === 'Enter' && handleQuickAddPuerto()}
                                />
                              </div>
                              <Button onClick={handleQuickAddPuerto} className="w-full">
                                {t('shipments.addPortBtn')}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('shipments.incoterm')}</Label>
                    <Select 
                      value={editForm.incoterm_code || ''} 
                      onValueChange={(v) => setEditForm({...editForm, incoterm_code: v})}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t('shipments.selectPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {incoterms.map(i => (
                          <SelectItem key={i.code} value={i.code}>{i.code} - {i.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('shipments.vessel')}</Label>
                    <Input
                      value={editForm.vessel_name || ''}
                      onChange={(e) => setEditForm({...editForm, vessel_name: e.target.value})}
                      disabled={isReadOnly}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('shipments.cargoTypeLabel')}</Label>
                    <Input
                      value={editForm.cargo_type || ''}
                      onChange={(e) => setEditForm({...editForm, cargo_type: e.target.value})}
                      disabled={isReadOnly}
                      className="mt-1"
                      placeholder={t('shipments.cargoTypePh')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('shipments.generalStatusLabel')}</Label>
                    <Select 
                      value={editForm.status_general_code || ''} 
                      onValueChange={(v) => setEditForm({...editForm, status_general_code: v})}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t('shipments.selectPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {shipmentStatuses.map(s => (
                          <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {!isReadOnly && (
                    <div className="flex items-center gap-2 pt-6">
                      <Switch
                        checked={editForm.is_active}
                        onCheckedChange={(v) => setEditForm({...editForm, is_active: v})}
                      />
                      <Label>{t('shipments.activeLabel')}</Label>
                    </div>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="bg-card border border-border rounded-sm p-4 space-y-4">
                <h3 className="font-heading text-lg">{t('shipments.datesAndEtas')}</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('shipments.etdOrigin')}</Label>
                    <Input
                      type="date"
                      value={editForm.etd_final_destination || ''}
                      onChange={(e) => setEditForm({...editForm, etd_final_destination: e.target.value})}
                      disabled={isReadOnly}
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('shipments.originDepartureDate')}</p>
                  </div>
                  <div>
                    <Label>{t('shipments.docsSentToAgent')}</Label>
                    <Input
                      type="date"
                      value={editForm.docs_sent_at?.slice(0, 10) || ''}
                      onChange={(e) => setEditForm({...editForm, docs_sent_at: e.target.value || null})}
                      disabled={isReadOnly}
                      className="mt-1"
                      data-testid="docs-sent-at-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('shipments.docsSendDate')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('shipments.initialPortEta')}</Label>
                    <Input
                      type="date"
                      value={editForm.eta_port || ''}
                      onChange={(e) => setEditForm({...editForm, eta_port: e.target.value})}
                      disabled={isReadOnly}
                      className="mt-1"
                      data-testid="eta-port-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('shipments.initialArrivalDate')}</p>
                  </div>
                  <div>
                    <Label>{t('shipments.realPortEta')}</Label>
                    <Input
                      type="date"
                      value={editForm.eta_real?.slice(0, 10) || ''}
                      onChange={(e) => setEditForm({...editForm, eta_real: e.target.value || null})}
                      disabled={isReadOnly}
                      className="mt-1"
                      data-testid="eta-real-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('shipments.realArrivalDate')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('shipments.estimatedDeliveryEta')}</Label>
                    {shipment.eta_entrega_estimada ? (
                      <div className="mt-1 p-2 bg-primary/10 border border-primary/20 rounded-md">
                        <span className="text-sm font-medium text-primary">
                          {formatDate(shipment.eta_entrega_estimada)}
                        </span>
                        <p className="text-xs text-muted-foreground mt-1">{t('shipments.autoCalculated')}</p>
                      </div>
                    ) : (
                      <Input
                        type="date"
                        value={editForm.eta_final || ''}
                        onChange={(e) => setEditForm({...editForm, eta_final: e.target.value})}
                        disabled={isReadOnly}
                        className="mt-1"
                        data-testid="eta-final-input"
                      />
                    )}
                    <p className="text-xs text-muted-foreground mt-1">{t('shipments.estimatedDeliveryDate')}</p>
                  </div>
                  <div>
                    <Label>{t('shipments.realDeliveryLabel')}</Label>
                    <Input
                      type="date"
                      value={editForm.delivered_at?.slice(0, 10) || ''}
                      onChange={(e) => setEditForm({...editForm, delivered_at: e.target.value || null})}
                      disabled={isReadOnly}
                      className="mt-1"
                      data-testid="delivered-at-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{t('shipments.realDeliveryDate')}</p>
                  </div>
                </div>

                <div className="pt-2 text-sm text-muted-foreground">
                  <p>{t('shipments.createdAtLabel')}: {formatDateTime(shipment.created_at)}</p>
                  <p>{t('shipments.lastUpdateLabel')}: {formatDateTime(shipment.last_update_at)}</p>
                </div>
              </div>

              {/* Importer/Supplier - Only for ops */}
              {!isReadOnly && (
                <div className="bg-card border border-border rounded-sm p-4 space-y-4">
                  <h3 className="font-heading text-lg">{t('shipments.importerSupplier')}</h3>
                  
                  <div>
                    <Label>{t('shipments.client')}</Label>
                    <Select 
                      value={editForm.client_id || ''} 
                      onValueChange={(v) => setEditForm({...editForm, client_id: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t('shipments.selectPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('shipments.importer')}</Label>
                    <Select 
                      value={editForm.importer_id || ''} 
                      onValueChange={(v) => setEditForm({...editForm, importer_id: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t('shipments.selectPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('shipments.notAssigned')}</SelectItem>
                        {importers.map(i => (
                          <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('shipments.supplier')}</Label>
                    <Select 
                      value={editForm.supplier_id || ''} 
                      onValueChange={(v) => setEditForm({...editForm, supplier_id: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={t('shipments.selectPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('shipments.notAssigned')}</SelectItem>
                        {suppliers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Cost & Internal flags */}
              <div className="bg-card border border-border rounded-sm p-4 space-y-4">
                {hasField('view_costs') && (
                <>
                <h3 className="font-heading text-lg">{t('shipments.operationCost')}</h3>
                
                <div className="flex items-center gap-4">
                  <CostBadge calculated={shipment.cost_calculated} cost={shipment.total_cost} />
                </div>

                {!isReadOnly && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>{t('shipments.totalCostUsd')}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editForm.total_cost || ''}
                          onChange={(e) => setEditForm({...editForm, total_cost: e.target.value})}
                          className="mt-1 font-mono"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <Switch
                          checked={editForm.cost_calculated}
                          onCheckedChange={(v) => setEditForm({...editForm, cost_calculated: v})}
                        />
                        <Label>{t('shipments.costCalculated')}</Label>
                      </div>
                    </div>

                    <div>
                      <Label>{t('shipments.breakdownImage')}</Label>
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setCostImage(e.target.files[0])}
                          className="flex-1"
                        />
                        <Button 
                          variant="secondary" 
                          onClick={handleUploadCostImage}
                          disabled={!costImage}
                        >
                          <Upload size={16} className="mr-1" />
                          {t('shipments.uploadBtn')}
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                {shipment.cost_breakdown_image && (
                  <div className="mt-4">
                    <Label>{t('shipments.currentBreakdown')}</Label>
                    <Dialog>
                      <DialogTrigger asChild>
                        <img 
                          src={shipment.cost_breakdown_image} 
                          alt={t('shipments.costBreakdown')}
                          className="mt-2 max-h-40 rounded border border-border cursor-pointer hover:opacity-80 transition-opacity"
                        />
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle>{t('shipments.costBreakdown')}</DialogTitle>
                        </DialogHeader>
                        <img 
                          src={shipment.cost_breakdown_image} 
                          alt={t('shipments.costBreakdown')}
                          className="w-full rounded"
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
                </>
                )}

                {/* Internal flags - only ops */}
                {!isReadOnly && (
                  <div className="pt-4 border-t border-border space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground">{t('shipments.internalFields')}</h4>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editForm.final_quote_done}
                        onCheckedChange={(v) => setEditForm({...editForm, final_quote_done: v})}
                      />
                      <Label>{t('shipments.finalQuoteDone')}</Label>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editForm.arnulfo_applies}
                        onCheckedChange={(v) => setEditForm({...editForm, arnulfo_applies: v, arnulfo_status: v ? 'PENDIENTE' : 'NO_APLICA'})}
                      />
                      <Label>{t('shipments.arnulfoApplies')}</Label>
                    </div>

                    {editForm.arnulfo_applies && (
                      <div>
                        <Label>{t('shipments.arnulfoStatus')}</Label>
                        <Select 
                          value={editForm.arnulfo_status || 'PENDIENTE'} 
                          onValueChange={(v) => setEditForm({...editForm, arnulfo_status: v})}
                        >
                          <SelectTrigger className="mt-1 w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PENDIENTE">{t('shipments.arnulfoPending')}</SelectItem>
                            <SelectItem value="PAGADO">{t('shipments.arnulfoPaid')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Containers Tab */}
          <TabsContent value="containers" className="space-y-4">
            <ContainerTable
              containers={containers}
              containerStatuses={containerStatuses}
              isReadOnly={isReadOnly}
              isMobile={isMobile}
              onUpdateContainer={handleUpdateContainerWithReload}
              onDeleteContainer={handleDeleteContainer}
              onAddContainersBulk={handleAddContainersBulk}
              onReloadData={reloadContainersAndShipment}
            />
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <EventsPanel
              events={events}
              eventTypes={eventTypes}
              containers={containers}
              newEvent={newEvent}
              setNewEvent={setNewEvent}
              onAdd={handleAddEvent}
              onDelete={handleDeleteEvent}
              isReadOnly={isReadOnly}
            />
          </TabsContent>

          {/* Costs Tab */}
          {/* Costs Tab - Only ops/admin */}
          {!isReadOnly && (
            <TabsContent value="costs" className="space-y-4">
              {visitedTabs.has('costs') && <CostBreakdownPanel mbl={mbl} isReadOnly={isReadOnly} />}
            </TabsContent>
          )}

          {/* Notes Tab - Visible to all (clients see only SOIA/client-visible notes) */}
            <TabsContent value="notes" className="space-y-4">
              {isReadOnly && hasFeature('client_force_soia') && (
                <div className="bg-card border border-border rounded-sm p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-muted-foreground">
                    Consulta el estatus aduanal (SOIA/SAT) de tus pedimentos al instante.
                  </div>
                  <Button
                    onClick={handleClientConsultSoia}
                    disabled={clientConsultingSoia}
                    data-testid="client-consult-soia-btn"
                  >
                    {clientConsultingSoia ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <RefreshCw size={16} className="mr-2" />
                    )}
                    {clientConsultingSoia ? 'Consultando...' : 'Consultar SOIA ahora'}
                  </Button>
                </div>
              )}
              <NotesPanel
                notes={notes}
                containers={containers}
                newNote={newNote}
                setNewNote={setNewNote}
                onAdd={handleAddNote}
                onDelete={handleDeleteNote}
                isReadOnly={isReadOnly}
              />
            </TabsContent>

          {/* History Tab - Only ops/admin */}
          {!isReadOnly && (
            <TabsContent value="pedimentos" className="space-y-4">
              <div className="bg-card border border-border rounded-sm p-4">
                <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <FileText size={18} />
                  {t('shipments.pedimentosHeader')}
                </h3>
                {visitedTabs.has('pedimentos') && (
                  <PedimentoPanel shipmentMbl={mbl} containers={containers} isAdmin={!isReadOnly} />
                )}
              </div>
            </TabsContent>
          )}

          {/* Documents Tab */}
          {!isReadOnly && (
            <TabsContent value="documents" className="space-y-4">
              <div className="bg-card border border-border rounded-sm p-4">
                <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <FileText size={18} />
                  {t('shipments.shipmentDocs')}
                </h3>
                {visitedTabs.has('documents') && (
                  <DocumentsPanel shipmentMbl={mbl} containers={containers} isAdmin={!isReadOnly} />
                )}
              </div>
              {visitedTabs.has('documents') && <ContractPanel mbl={mbl} />}
            </TabsContent>
          )}

          {/* History Tab - Only ops/admin */}
          {!isReadOnly && (
            <TabsContent value="history" className="space-y-4">
              <div className="bg-card border border-border rounded-sm p-4">
                <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <History size={18} />
                  {t('shipments.changeHistory')}
                </h3>
                {visitedTabs.has('history') && <AuditHistoryPanel mbl={mbl} />}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
